/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import type { RotaSettings } from "./types";

type Props = { settings: RotaSettings; onClose: (changed: boolean) => void };

const FIELDS: { key: keyof RotaSettings; unit: string; step: number }[] = [
  { key: "max_hours_day", unit: "h", step: 0.5 },
  { key: "max_hours_week", unit: "h", step: 0.5 },
  { key: "min_rest_hours", unit: "h", step: 0.5 },
  { key: "max_consecutive_days", unit: "d", step: 1 },
  { key: "publish_days_ahead", unit: "d", step: 1 },
];

// Labour-rule limits. The rota only warns when a plan breaks one.
export const RulesModal = defineComponent<Props>({
  name: "RulesModal",
  props: ["settings", "onClose"],
  setup(props) {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const values = ref<RotaSettings>({ ...props.settings });
    const saving = ref(false);

    const save = async () => {
      saving.value = true;
      try {
        await trpc.rota.settings.save.mutate(values.value);
        toast.showToast(t("workday.common.saved"), { type: "success" });
        props.onClose(true);
      } catch (e) {
        toast.showToast((e as { message?: string })?.message ?? t("workday.common.save_failed"), { type: "error" });
      } finally {
        saving.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("workday.team.rules_title")}
        onClose={() => props.onClose(false)}
        renderButtons={() => (
          <div class="border-t p-4">
            <div class="flex justify-between gap-2">
              <button class="btn btn-neutral" onClick={() => props.onClose(false)}>
                {t("general.words.cancel")}
              </button>
              <button class="btn btn-primary" disabled={saving.value} onClick={save}>
                {t("workday.common.save")}
              </button>
            </div>
          </div>
        )}
      >
        <div class="space-y-4 p-6">
          <div class="alert py-2 text-sm">
            <span class="material-symbols-outlined">info</span>
            {t("workday.team.rules_help")}
          </div>
          {FIELDS.map((f) => (
            <label key={f.key} class="flex items-center justify-between gap-4">
              <span>
                <span class="block text-sm font-medium">{t(`workday.rules.${f.key}`)}</span>
                <span class="block text-xs text-base-content/50">{t(`workday.rules.${f.key}_hint`)}</span>
              </span>
              <span class="join">
                <input
                  type="number"
                  min="0"
                  step={f.step}
                  class="input join-item input-bordered input-sm w-20 text-right"
                  value={values.value[f.key]}
                  onChange={(e: Event) => (values.value = { ...values.value, [f.key]: Number((e.target as HTMLInputElement).value) })}
                />
                <span class="join-item flex w-8 items-center justify-center bg-base-200 text-xs">{f.unit}</span>
              </span>
            </label>
          ))}
        </div>
      </LemmonModal>
    );
  },
});
